import React, { useEffect } from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import AddToCartButton from './AddToCartButton.tsx'
import { getSmallAssetPath } from '@aytm/lib/common/assets.ts'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/utils/formatPriceWithLocale.ts'
import { EcommerceResponse, IEcommerceQuestionSchema } from '@aytm/app/swidget/base/questions/Ecommerce/types'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale.ts'
import style from './Cart.module.scss'

interface ICartProps {
  response: EcommerceResponse
  schema: IEcommerceQuestionSchema
  totals?: number[]
  findability: boolean
  renderError: () => React.ReactNode
  onAddToCart: (qnt: 1 | -1, index: number) => void
  onItemSelect: (index: number) => void
  onNaSelect: () => void
  checkout?: () => void
  scrollTop: () => void
}

const Cart = ({
  response,
  schema,
  totals,
  findability,
  renderError,
  onAddToCart,
  onItemSelect,
  onNaSelect,
  checkout,
  scrollTop
}: ICartProps) => {
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()
  useEffect(() => {
    scrollTop()
  }, [scrollTop])
  const na = schema.answers.find(answer => answer.na)
  const valid =
    (schema.max_money_spent ? totals[1] <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? totals[0] <= schema.max_cart_size : true)
  return (
    <section className={`${style.cart} ${viewport.mobile ? style.cartMobile : ''}`}>
      <div className={style.products}>
        <h6>{t('questions', 'ec', 'ecommerce', 'cart')}</h6>
        {totals[0] > 0 ? (
          <>
            {response.answers.map(
              (answer, index) =>
                answer.v > 0 && (
                  <div className={style.product} key={index}>
                    <div className={style.content}>
                      <div className={style.imageWrapper}>
                        {schema.answers[index].image ? (
                          <div
                            onClick={() => onItemSelect(index)}
                            className={style.image}
                            role="img"
                            aria-label={schema.answers[index].image?.alt_text || t('questions', 'img', 'noAlt')}
                            style={{
                              backgroundImage: `url(${getSmallAssetPath(schema.answers[index].image)})`
                            }}
                          />
                        ) : (
                          <div className={style.noImage} onClick={() => onItemSelect(index)} />
                        )}
                        <div className={style.buttons}>
                          <AddToCartButton
                            quantity={answer.v}
                            findability={findability}
                            index={index}
                            size="small"
                            onAddToCart={onAddToCart}
                            limit={schema.max_qty_per_product}
                          />
                          <EmptyLink color="#111">
                            <div className={style.fav}>
                              <div className={style.heartIcon} />
                            </div>
                          </EmptyLink>
                        </div>
                      </div>
                      <div className={style.info}>
                        <div
                          className={style.caption}
                          dangerouslySetInnerHTML={{ __html: schema.answers[index].text }}
                          onClick={() => onItemSelect(index)}
                        />
                        <div className={style.prices}>
                          {schema.answers[index].discounted_price &&
                          schema.answers[index].price > schema.answers[index].discounted_price ? (
                            <>
                              <div
                                className={style.newPrice}
                                dangerouslySetInnerHTML={{
                                  __html: `${formatPriceWithLocale(
                                    schema.answers[index].discounted_price * answer.v,
                                    schema.price_mask,
                                    languageTag,
                                    true,
                                    false
                                  )}`
                                }}
                              />
                              <div
                                className={style.lastPrice}
                                dangerouslySetInnerHTML={{
                                  __html: `${formatPriceWithLocale(
                                    schema.answers[index].price * answer.v,
                                    schema.price_mask,
                                    languageTag,
                                    true,
                                    false
                                  )}`
                                }}
                              />
                              <div
                                className={style.off}
                                dangerouslySetInnerHTML={{
                                  __html: t<(off: string, applied: boolean) => string>(
                                    'questions',
                                    'ec',
                                    'ecommerce',
                                    'off'
                                  )(
                                    (
                                      ((schema.answers[index].price - schema.answers[index].discounted_price) /
                                        schema.answers[index].price) *
                                      100
                                    ).toFixed(1),
                                    false
                                  )
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <div
                                className={style.defaultPrice}
                                dangerouslySetInnerHTML={{
                                  __html: `${formatPriceWithLocale(
                                    schema.answers[index].price * answer.v,
                                    schema.price_mask,
                                    languageTag,
                                    true,
                                    false
                                  )}`
                                }}
                              />
                            </>
                          )}
                        </div>
                        {answer.v > 1 && (
                          <div
                            className={style.perCount}
                            dangerouslySetInnerHTML={{
                              __html: t<(price: string) => string>(
                                'questions',
                                'ec',
                                'ecommerce',
                                'pricePerCount'
                              )(
                                formatPriceWithLocale(
                                  schema.answers[index].discounted_price || schema.answers[index].price,
                                  schema.price_mask,
                                  languageTag,
                                  true,
                                  false
                                )
                              )
                            }}
                          />
                        )}
                        <div className={style.title}>{t('questions', 'ec', 'ecommerce', 'shipping')}</div>
                        <div className={style.arrives}>
                          <span
                            className={style.shippingDate}
                            dangerouslySetInnerHTML={{
                              __html: t<(date: string) => string>(
                                'questions',
                                'ec',
                                'ecommerce',
                                'arrivesBy'
                              )(
                                new Date(new Date().setDate(new Date().getDate() + 6)).toLocaleDateString(languageTag, {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric'
                                })
                              )
                            }}
                          />
                          <EmptyLink color="#111">
                            <span className={style.textUnderlined}>
                              {t('questions', 'ec', 'ecommerce', 'editLocation')}
                            </span>
                          </EmptyLink>
                        </div>
                        <div className={style.pickup}>
                          <div className={style.title}>{t('questions', 'ec', 'ecommerce', 'freePickup')}</div>
                          <EmptyLink color="#111">
                            <span className={style.textUnderlined}>
                              {t('questions', 'ec', 'ecommerce', 'findStore')}
                            </span>
                          </EmptyLink>
                        </div>
                      </div>
                    </div>
                    {viewport.mobile &&
                    schema.answers[index].discounted_price &&
                    schema.answers[index].discounted_price &&
                    schema.answers[index].price > schema.answers[index].discounted_price ? (
                      <div className={style.promo}>
                        <div className={style.promoIcon} />
                        <span
                          className={style.offText}
                          dangerouslySetInnerHTML={{
                            __html: t<(off: string, applied: boolean) => string>(
                              'questions',
                              'ec',
                              'ecommerce',
                              'off'
                            )(
                              (
                                ((schema.answers[index].price - schema.answers[index].discounted_price) /
                                  schema.answers[index].price) *
                                100
                              ).toFixed(1),
                              true
                            )
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                )
            )}
          </>
        ) : (
          <div className={style.emptyCart}>
            {viewport.mobile ? (
              <>
                <div className={style.bag} />
                <div
                  className={style.emptyText}
                  dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'ecommerce', 'emptyCartMobile') }}
                />
                {renderError()}
                {na && (
                  <button className={style.na} onClick={onNaSelect}>
                    {na.text || t('questions', 'ec', 'na')}
                  </button>
                )}
              </>
            ) : (
              <>
                <p dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'ecommerce', 'emptyCart') }} />
                {na && (
                  <button className={style.na} onClick={onNaSelect}>
                    {na.text || t('questions', 'ec', 'na')}
                  </button>
                )}
              </>
            )}
          </div>
        )}
        <div className={style.bigTitle}>{t('questions', 'ec', 'ecommerce', 'favTitle')}</div>
        <div className={style.favoritesText}>
          <div className={style.favText}>{t('questions', 'ec', 'ecommerce', 'favoritesText')}&nbsp;</div>
          <EmptyLink color="#111">
            <span className={style.favLink}>{t('questions', 'ec', 'ecommerce', 'joinUs')}</span>
          </EmptyLink>
          <span className={style.favText}>&nbsp;{t('questions', 'ec', 'ecommerce', 'or')}&nbsp;</span>
          <EmptyLink color="#111">
            <span className={style.favLink}>{t('questions', 'ec', 'ecommerce', 'signIn')}</span>
          </EmptyLink>
        </div>
      </div>
      {(!viewport.mobile || (viewport.mobile && totals[0] > 0)) && (
        <div className={style.checkout}>
          <h6>{t('questions', 'ec', 'ecommerce', 'summary')}</h6>
          <div className={style.summary}>
            <EmptyLink color="#111">
              <div className={style.row}>
                <span className={style.textMedium}>{t('questions', 'ec', 'ecommerce', 'havePromo')}</span>
                <div className={style.downIcon} />
              </div>
            </EmptyLink>
            <div className={style.row}>
              <div className={style.textLight}>
                <span>{t('questions', 'ec', 'ecommerce', 'subtotal')}</span>
                <EmptyLink color="#111">
                  <div className={style.signIcon} />
                </EmptyLink>
              </div>
              <div
                className={style.textLight}
                dangerouslySetInnerHTML={{
                  __html: `${totals[1] > 0 ? formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false) : '—'}`
                }}
              />
            </div>
            <div className={style.row}>
              <div className={style.textLight}>{t('questions', 'ec', 'ecommerce', 'handling')}</div>
              <div className={style.textLight}>{t('questions', 'ec', 'ecommerce', 'free')}</div>
            </div>
            <div className={style.row}>
              <div className={style.textLight}>
                <span>{t('questions', 'ec', 'ecommerce', 'tax')}</span>
                <EmptyLink color="#111">
                  <div className={style.signIcon} />
                </EmptyLink>
              </div>
              <div className={style.textLight}>—</div>
            </div>
            <div className={style.line} />
            <div className={style.row}>
              <div className={style.textMedium}>
                <span>{t('questions', 'ec', 'ecommerce', 'total')}</span>
              </div>
              <div
                className={style.textMedium}
                dangerouslySetInnerHTML={{
                  __html: `${totals[0] > 0 ? formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false) : '—'}`
                }}
              />
            </div>
            {!viewport.mobile && <div className={style.line} />}
            <div className={style.checkoutTextWrapper}>
              <div
                className={style.checkoutText}
                dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'ecommerce', 'checkoutText') }}
              />
              <EmptyLink color="#111">
                <span className={style.checkoutLink}>{t('questions', 'ec', 'ecommerce', 'joinUs')}</span>
              </EmptyLink>
              <span className={style.checkoutText}>&nbsp;or&nbsp;</span>
              <EmptyLink color="#111">
                <span className={style.checkoutLink}>{t('questions', 'ec', 'ecommerce', 'signIn')}</span>
              </EmptyLink>
            </div>
            {renderError()}
            <button
              className={style.checkoutButton}
              onClick={totals[0] > 0 ? checkout : null}
              disabled={0 === totals[0] || !valid}
            >
              {t('questions', 'ec', 'ecommerce', 'checkout')}
            </button>
            {renderError()}
            <div
              className={style.disclaimer}
              dangerouslySetInnerHTML={{
                __html: t<(store: string) => string>('questions', 'ec', 'ecommerce', 'disclaimer')(schema.name)
              }}
            />
          </div>
        </div>
      )}
    </section>
  )
}

export default Cart
